import { useQueryClient } from 'react-query';
import { LimitedSchemaMutationOptions, useMutation } from '@frontend/react-query-helpers';
import { SchemaDigitalForms } from '@frontend/schema';
import { endpointKeys as queryEndpointKeys } from '../../queries';
import { IO } from '../../types';
import { endpointKeys } from '../keys';

const FormsQueryKeys = {
  formSubmission: 'wf-form-submission',
  formSubmissions: 'wf-form-submissions',
  submissionCategoryCount: 'wf-submission-category-count',
};

export const useArchiveSubmissionsV2 = <E = unknown, C = unknown>(
  options?: LimitedSchemaMutationOptions<IO.UpdateSubmissionsArchiveStatusV2, E, C>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: endpointKeys.updateSubmissionsArchiveStatusV2,
    mutationFn: (payload) => SchemaDigitalForms.UpdateSubmissionsArchiveStatusV2(payload),
    ...options,
    onSuccess: async (...successArgs) => {
      await Promise.all([
        queryClient.invalidateQueries(queryEndpointKeys.submissionsList),
        queryClient.invalidateQueries(queryEndpointKeys.getSubmissionDetailsById),

        // TODO: These 3 queries are deprecated and should be removed after SubmissionV2 is fully migrated.
        queryClient.invalidateQueries(FormsQueryKeys.formSubmissions),
        queryClient.invalidateQueries(FormsQueryKeys.formSubmission),
        queryClient.invalidateQueries(FormsQueryKeys.submissionCategoryCount),
      ]);

      if (options?.onSuccess) {
        options.onSuccess(...successArgs);
      }
    },
  });
};

interface UpdateSubmissionIsReadPayloadIO {
  input: IO.UpdateSubmissionIsRead['input'] & {
    // Location ID is required to passed as header to update the submission read status
    locationId: string;
  };
  output: IO.UpdateSubmissionIsRead['output'];
}

export const useUpdateSubmissionIsRead = <E = unknown, C = unknown>(
  options?: LimitedSchemaMutationOptions<UpdateSubmissionIsReadPayloadIO, E, C>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: endpointKeys.updateSubmissionIsReadStatus,
    mutationFn: ({ locationId, ...otherPayload }) =>
      SchemaDigitalForms.UpdateSubmissionIsRead(otherPayload, {
        headers: { 'location-id': locationId },
      }),
    ...options,
    onSuccess: async (...successArgs) => {
      await Promise.all([
        queryClient.invalidateQueries(queryEndpointKeys.submissionsList),
        queryClient.invalidateQueries(queryEndpointKeys.getSubmissionDetailsById),
      ]);

      if (options?.onSuccess) {
        options.onSuccess(...successArgs);
      }
    },
  });
};
